import React, { useState, useEffect, useCallback } from "react";
import { AuthContext } from "./contextExporter";
import { lsUserKey } from "../utils/static";
import { convertTokenToUser, getValidUserFromJWT } from "../utils/common";
import { refreshTokenFn } from "../api/auth";
import { reportSessionInfo } from "../utils/deviceInfo";
import { useSocket } from "../hooks/useSocket";
import { useQueryClient } from "@tanstack/react-query";

export interface IUser {
  id: number;
  cohort_id: number;
  cohort_name?: string;
  username: string;
  shortName?: string;
  role: number;
  email: string;
  tempo: "fulltimeSelf" | null
  education_type?: string
  demo_end_date?: string | null;
}

export function AuthContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<null | IUser>(null);
  // Kullanıcı yüklenene kadar sayfa durumunu izlemek için
  const [loading, setLoading] = useState<boolean>(true);
  const { connectSocket, disconnectSocket } = useSocket();
  const queryClient = useQueryClient();
  const isInstructor = user?.role === 34;
  const isFulltimeSelfUser = user?.tempo === "fulltimeSelf"

  // Sayfa yüklendiğinde localStorage'dan kullanıcı verilerini al
  useEffect(() => {
    const checkUserAuth = async () => {
      const user = await getValidUserFromJWT();
      if (user) {
        setUser(user);
        connectSocket(user);
        // zaten login olmuş kullanıcının cihaz/oturum bilgisini bildir (fire-and-forget)
        reportSessionInfo();
      }
      setLoading(false);
    };

    checkUserAuth();
  }, [connectSocket]);

  // Backend'den guncel user bilgisini cekip state'i tazele.
  // Admin cohort degistirdiginde ciks-gir gerekmesin diye.
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem(lsUserKey);
    if (!token) return;
    try {
      const newToken = await refreshTokenFn({ token });
      const refreshed = convertTokenToUser(newToken);
      setUser((prev) => {
        // cohort_id degistiyse react-query cache'i temizle ki eski cohort'un
        // icerigi ekranda kalmasin
        if (prev && prev.cohort_id !== refreshed.cohort_id) {
          queryClient.invalidateQueries();
        }
        return refreshed;
      });
    } catch (err) {
      console.error("refreshUser failed", err);
    }
  }, [queryClient]);

  // Sekmeye geri donuldugunde user bilgisini tazele (cohort degisikligi vb.)
  useEffect(() => {
    if (!user) return;
    const onFocus = () => {
      if (document.visibilityState === "visible") refreshUser();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [user, refreshUser]);

  const login = (jwt: string) => {
    const user = convertTokenToUser(jwt);
    setUser(user);
    localStorage.setItem(lsUserKey, jwt);
    connectSocket(user);
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem(lsUserKey);
    disconnectSocket();
    queryClient.clear();
  };

  if (loading) {
    return <div>Yükleniyor...</div>; // veya null ya da başka bir loading componenti
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        refreshUser,
        isInstructor,
        isFulltimeSelfUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
